// TES-1034 (Emelie, points 2 and 3) — the monthly review's Webdoc step. The
// renewal may not be completed without its journal note, even when no care
// meeting is held, and nothing is published to the patient before the
// compiled note has been copied to Webdoc. So every decision in the review
// (the primary publish-and-renew, confirming without feedback, rejecting,
// publishing the feedback alone) waits for the same things: the compiled
// note shown in the step, the tick that it is entered in Webdoc and, when the
// review's komplettering asked for a new blood test, the tick that the
// remiss is placed. The API refuses the decision without them and stores the
// note's final version and receipt with it.
//
// Pure on purpose: no React, no path aliases. The unit tests in
// `apps/admin/test` run it with plain `node --test`.
import type { WebdocControl } from "@care-by-ella/contracts";
import { requisitionRefControl } from "./requisition-ref";

export interface RenewalWebdocState {
  /** The compiled note as the step shows it; null until the step is opened. */
  note: string | null;
  /** The tick for the note being entered in Webdoc. */
  journalDone: boolean;
  /**
   * Whether the review's komplettering asked for a new blood test
   * (monthlyReviewNeedsLabOrder): the remiss row shows only then.
   */
  needsLabOrder: boolean;
  /** The tick for the lab order/remiss being placed in Webdoc. */
  labOrderDone: boolean;
  /** TES-442 — "Remissnummer i Webdoc (valfritt)", as typed. */
  requisitionRef?: string;
}

/**
 * Why a decision in the monthly review cannot be sent yet, as the button's
 * title says it; null when it can. Checked before the review's own
 * requirements (order, feedback text, phase decision).
 */
export function renewalWebdocBlocker(s: RenewalWebdocState): string | null {
  if (s.note === null) {
    return "Sammanställ journalanteckningen först";
  }
  if (!s.note.trim()) {
    return "Journalanteckningen är tom — skriv under rätt rubrik först";
  }
  if (!s.journalDone) {
    return "Bekräfta att journalanteckningen är införd i Webdoc";
  }
  if (s.needsLabOrder && !s.labOrderDone) {
    return "Bekräfta att provbeställningen/remissen är klar i Webdoc";
  }
  return null;
}

/** The `webdocControl` every monthly review decision sends. */
export function renewalWebdocControl(s: RenewalWebdocState): WebdocControl {
  return {
    journalInWebdoc: s.journalDone,
    ...(s.needsLabOrder ? { labOrderDoneInWebdoc: s.labOrderDone } : {}),
    ...requisitionRefControl(s),
  };
}

/**
 * The note the doctor confirmed is the note the API will store only while
 * nothing has changed since the step compiled it. When the compiled text at
 * the moment of the decision differs, the tick no longer covers it: the step
 * shows the new text and asks again.
 */
export function confirmedNoteStillCurrent(
  shown: string | null,
  current: string,
): boolean {
  return shown !== null && shown === current;
}

/**
 * TES-1063 — an input of the compiled note (the prescription status, its
 * valid-until date, the order, the komplettering, the follow-up) changed
 * while the step is open, so the note the step shows is no longer the note
 * the API would store. The step shows the new text at once; null when
 * nothing changed. `reconfirm` is whether the old text may already be in
 * Webdoc (it was copied or ticked): the tick is then taken back and the
 * doctor is told to copy again now, not when she presses publish. The
 * publish-time check (confirmedNoteStillCurrent) stays as the backstop for
 * what this cannot see: a journal box edited after the step compiled.
 */
export function noteInputChanged(
  shown: string | null,
  live: string | null,
  s: { journalDone: boolean; copied: boolean },
): { reconfirm: boolean } | null {
  if (shown === null || live === null || shown === live) return null;
  return { reconfirm: s.journalDone || s.copied };
}
